import type { ReactNode } from "react";

export type TopbarProps = {
  title: string;
  subtitle?: string;
  /** Action(s) shown inside the white "control card" on the right. */
  action?: ReactNode;
  /** Optional secondary row rendered below the primary control card. */
  secondary?: ReactNode;
  /** Status pill shown next to the title (e.g. role chip). */
  badge?: ReactNode;
};

/**
 * Sticky top bar for the dashboard. Mirrors the ESYS ContentHeader pattern:
 * a white shell with a bordered, blue-tinted "control card" containing the
 * page title, optional badge, and any quick actions (org filters etc.).
 */
export function Topbar({ title, subtitle, action, secondary, badge }: TopbarProps) {
  return (
    <header className="shrink-0 border-b border-[var(--color-border)] bg-[var(--color-surface)]">
      <div className="px-5 py-3">
        <div
          className="rounded-xl border px-4 py-3 shadow-sm"
          style={{
            borderColor: "var(--color-border-blue-tint)",
            background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
          }}
        >
          <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h1 className="truncate text-lg font-semibold tracking-tight text-[var(--color-text)] sm:text-xl">
                  {title}
                </h1>
                {badge}
              </div>
              {subtitle && (
                <p className="mt-0.5 truncate text-xs text-[var(--color-text-muted)]">
                  {subtitle}
                </p>
              )}
            </div>
            {action && (
              <div className="flex items-center justify-end gap-2 flex-wrap shrink-0 xl:border-l xl:border-[#e8eef8] xl:pl-3">
                {action}
              </div>
            )}
          </div>
          {secondary && <div className="mt-2">{secondary}</div>}
        </div>
      </div>
    </header>
  );
}
